import React, { useCallback, useEffect, useRef } from 'react';
import HarbourButton from '../../components/harbour/HarbourButton';
import './familyConfirmSheet.css';

/*
 * Family portal confirm sheet (Figma Harbour/Sheet · Mobile, Kind=Confirm,
 * with a Bottom bar Layout=Stacked footer): a bottom sheet over a scrim, a
 * title, a short body, then Cancel above the one primary action, nearest the
 * thumb and last in reading and focus order.
 *
 * Phone only. On desktop the same decision is a centred HarbourModal, which
 * carries its own ghost close; the caller picks by breakpoint.
 *
 * Contract, shared with the consent page's sheet: focus moves to the title on
 * open, Escape and a tap on the scrim do what Cancel does, and nothing closes
 * while the action is busy.
 */
export default function FamilyConfirmSheet({
  title,
  children,
  confirmLabel,
  cancelLabel = 'Cancel',
  confirmKind = 'family',
  // Off for a sheet whose one action is to close it (an information sheet).
  showCancel = true,
  onConfirm,
  onClose,
  busy = false,
}) {
  const titleRef = useRef(null);
  const close = useCallback(() => {
    if (!busy && typeof onClose === 'function') onClose();
  }, [busy, onClose]);

  useEffect(() => {
    if (titleRef.current) titleRef.current.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [close]);

  return (
    <div className="family-confirm-scrim" onClick={close} data-testid="family-confirm-scrim">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="family-confirm-sheet-title"
        className="family-confirm-sheet"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="family-confirm-sheet__grabber" aria-hidden="true" />
        <div className="family-confirm-sheet__header">
          <h2
            id="family-confirm-sheet-title"
            ref={titleRef}
            tabIndex={-1}
            className="family-confirm-sheet__title"
          >
            {title}
          </h2>
          {children && <div className="family-confirm-sheet__body">{children}</div>}
        </div>
        <div className="family-confirm-sheet__footer">
          {showCancel && (
            <HarbourButton
              kind="secondary"
              className="family-confirm-sheet__action"
              disabled={busy}
              onClick={close}
            >
              {cancelLabel}
            </HarbourButton>
          )}
          <HarbourButton
            kind={confirmKind}
            className="family-confirm-sheet__action"
            busy={busy}
            disabled={busy}
            aria-busy={busy}
            onClick={onConfirm}
          >
            {confirmLabel}
          </HarbourButton>
        </div>
      </div>
    </div>
  );
}
